/*
 * Countdown timer.
 *
 * Ticks down to an absolute deadline (an ISO 8601 string that carries its own UTC
 * offset, e.g. 2026-10-15T09:00:00+02:00), so the comparison is timezone-safe: every
 * visitor sees the same moment regardless of their local clock's zone. The visible
 * digits are aria-hidden — a per-second live region would spam assistive tech — so the
 * section heading carries the meaning for everyone.
 *
 * The largest *visible* unit absorbs all overflow above it: hide "days" and the "hours"
 * field counts past 24; hide days + hours and "minutes" counts past 60. No unit label,
 * heading or expired copy lives here — all of it is authored as translatable section
 * settings in the Liquid.
 */
if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        // The defining script runs before this element's children are parsed, so the
        // upgrade fires connectedCallback at the open tag — defer init until the DOM
        // is complete or the child fields don't exist yet to be queried.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
          this.init();
        }
      }

      init() {
        this.deadline = new Date(this.dataset.deadline).getTime();
        this.timerEl = this.querySelector('.countdown__timer');
        this.expiredEl = this.querySelector('.countdown__expired');
        this.activeCta = this.querySelector('.countdown__cta');

        // Descending order — the first field present becomes the overflow sink.
        this.divisor = { days: 86400, hours: 3600, minutes: 60, seconds: 1 };
        this.fields = Object.keys(this.divisor)
          .map((key) => ({ key, el: this.querySelector('[data-unit="' + key + '"] .countdown__value') }))
          .filter((f) => f.el);

        // A blank/invalid date or no visible fields means there is nothing to run;
        // leave the authored placeholder markup untouched rather than blanking it.
        if (!this.fields.length || isNaN(this.deadline)) return;

        this.tick = this.tick.bind(this);
        this.tick();
        this.interval = setInterval(this.tick, 1000);
      }

      disconnectedCallback() {
        if (this.interval) clearInterval(this.interval);
      }

      tick() {
        let remaining = Math.floor((this.deadline - Date.now()) / 1000);
        if (remaining <= 0) {
          this.finish();
          return;
        }

        this.fields.forEach((field) => {
          const secs = this.divisor[field.key];
          const value = Math.floor(remaining / secs);
          remaining -= value * secs;
          field.el.textContent = String(value).padStart(2, '0');
        });
      }

      finish() {
        if (this.interval) clearInterval(this.interval);
        // Idempotent: reaching zero swaps the live timer + its active CTA for the
        // optional "offer closed" message, if one was authored.
        if (this.expiredEl) {
          if (this.timerEl) this.timerEl.hidden = true;
          if (this.activeCta) this.activeCta.hidden = true;
          this.expiredEl.hidden = false;
        } else if (this.fields.length) {
          // No expired copy — just settle every field on zero.
          this.fields.forEach((field) => {
            field.el.textContent = '00';
          });
        }
      }
    }
  );
}
